import React, { useCallback, useEffect, useState } from 'react'
import { DragDropContext, Draggable, DropResult } from 'react-beautiful-dnd'
import { loader } from 'react-global-loader'
import { useNavigate } from 'react-router-dom'

import { Breadcrumb, PageTitle, Template } from '@components'
import Button from '@components/Button'
import Divider from '@components/Divider'
import EmptyRow from '@components/EmptyRow'
import Flex from '@components/Flex'
import SearchInput from '@components/SearchInput'
import Spinner from '@components/Spinner'
import Table from '@components/Table'
import Td from '@components/Td'
import Tr from '@components/Tr'
import { successMessages } from '@constants/feedbackMessages'
import {
	MenuAlt2Icon,
	PencilAltIcon,
	TrashIcon,
} from '@heroicons/react/outline'
import { Tutorial } from '@models/tutorial.model'
import { tutorialApi } from '@services/TutorialApi'
import { loadingModal, openAlertModal, openModal } from '@store/ducks/modal'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch } from '@store/hooks'
import { ruleOperatorLabels } from '@utils/helpers/tutorialAudience'
import { useFilters, useInputBounce, useQuery } from '@utils/hooks'

import AddTutorial from './AddTutorial'
import EditTutorial from './EditTutorial'

const styles = {
	searchContainer: 'gap-[8px] mb-[32px] items-end flex-wrap',
	divider: 'mt-[32px] mb-[12px]',
	audience: 'text-xs text-gray',
	draft: 'text-xs text-gray',
}

function describeAudience({ visibility, classes, rules }: Tutorial) {
	if (visibility === 'all') {
		return 'Todos os alunos'
	}

	const parts: string[] = []

	if (classes?.length) {
		parts.push(`${classes.length} ${classes.length === 1 ? 'turma' : 'turmas'}`)
	}

	rules?.forEach((rule) => {
		parts.push(`nome ${ruleOperatorLabels[rule.operator]} "${rule.value}"`)
	})

	return parts.length ? parts.join(' · ') : 'Ninguém — sem turma e sem regra'
}

function Tutorials() {
	const pages = [{ name: 'Tutoriais', href: '#', current: true }]
	const dispatch = useAppDispatch()
	const query = useQuery()
	const navigate = useNavigate()

	const searchQs = query.get('search') || ''

	const [search, setSearch] = useState(decodeURIComponent(searchQs))
	const [searchQuery, setSearchQuery] = useState(decodeURIComponent(searchQs))

	const { data, isLoading } = tutorialApi.useListQuery({
		search: encodeURIComponent(searchQuery),
	})

	const [
		deleteTutorial,
		{ isLoading: loadingDelete, isSuccess: deleteSuccess },
	] = tutorialApi.useDeleteMutation()

	const [reorderTutorial] = tutorialApi.useReorderMutation()

	const [tutorials, setTutorials] = useState(data?.data)

	const searchCallback = useCallback(
		(value: string) => {
			navigate('/tutoriais')
			setSearchQuery(value)
		},
		[navigate]
	)

	useEffect(() => {
		dispatch(loadingModal({ loading: loadingDelete }))

		if (deleteSuccess) {
			dispatch(notificationCreators.show(successMessages.deleteTutorial))
		}
	}, [dispatch, loadingDelete, deleteSuccess])

	useEffect(() => {
		setTutorials(data?.data)
	}, [data])

	useInputBounce(search, searchCallback)

	useFilters({
		search: searchQuery,
		fallbackUrl: '/tutoriais',
	})

	async function handleDragEnd(result: DropResult) {
		if (!tutorials || !result.destination) {
			return
		}

		const from = result.source.index
		const to = result.destination.index

		if (from === to) {
			return
		}

		// Com busca ativa a lista é um recorte, então o índice não é a posição.
		const newPosition = tutorials[to].position

		loader.show()

		try {
			await reorderTutorial({
				tutorialId: tutorials[from].id,
				newPosition,
			}).unwrap()

			const list = [...tutorials]
			const [removed] = list.splice(from, 1)

			list.splice(to, 0, removed)

			setTutorials(list)
		} catch {
			// O refetch da lista devolve a ordem que o servidor gravou.
		}

		loader.hide()
	}

	return (
		<>
			<Template>
				<Breadcrumb pages={pages} />
				<PageTitle title='Tutoriais' />
				<Flex className={styles.searchContainer}>
					<SearchInput
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className='flex-1'
						placeholder='Buscar tutorial'
					/>

					<Button onClick={() => dispatch(openModal({ name: 'addTutorial' }))}>
						Adicionar Tutorial
					</Button>
				</Flex>

				{isLoading ? (
					<div className='flex justify-center'>
						<Spinner size='md' />
					</div>
				) : (
					<DragDropContext onDragEnd={handleDragEnd}>
						<Table
							css='overflow-x-visible'
							th={[
								{ label: 'título' },
								{ label: 'quem vê' },
								{ label: 'visualizações' },
								{ label: 'ações' },
								{ label: 'ordem' },
							]}
							droppable
							variant='mixed'
						>
							{tutorials && tutorials.length > 0 ? (
								tutorials.map((tutorial, index) => (
									<Draggable
										key={tutorial.id}
										draggableId={String(tutorial.id)}
										index={index}
									>
										{(provided) => (
											<Tr
												ref={provided.innerRef}
												{...provided.draggableProps}
												{...provided.dragHandleProps}
											>
												<Td className='w-[40%] px-6'>
													{tutorial.title}
													{!tutorial.isPublished && (
														<span className={styles.draft}> · rascunho</span>
													)}
													<p className={styles.audience}>
														{tutorial.youtubeUrl}
													</p>
												</Td>
												<Td className='w-[30%] px-6'>
													{describeAudience(tutorial)}
												</Td>
												<Td className='px-6'>{tutorial.viewsCount}</Td>
												<Td>
													<Flex className='items-center gap-4'>
														<Button
															className='!px-2 !py-2'
															leftIcon={<PencilAltIcon />}
															variant='outline'
															onClick={() =>
																dispatch(
																	openModal({
																		name: 'editTutorial',
																		modalInfo: tutorial.id,
																	})
																)
															}
														>
															<></>
														</Button>
														<Button
															className='!px-2 !py-2'
															leftIcon={<TrashIcon />}
															variant='outline'
															onClick={() => {
																dispatch(
																	openAlertModal({
																		title: 'Remover Tutorial',
																		message: `Tem certeza que deseja remover "${tutorial.title}"?`,
																		buttonLabel: 'Remover Tutorial',
																		onClick: () =>
																			deleteTutorial({ id: tutorial.id }),
																	})
																)
															}}
														>
															<></>
														</Button>
													</Flex>
												</Td>
												<Td>
													<MenuAlt2Icon className='w-6 h-6' />
												</Td>
											</Tr>
										)}
									</Draggable>
								))
							) : (
								<EmptyRow tdAmount={5} message='Nenhum tutorial encontrado' />
							)}
						</Table>
					</DragDropContext>
				)}

				<Divider className={styles.divider} />
			</Template>

			<AddTutorial />
			<EditTutorial />
		</>
	)
}

export default Tutorials
